import React, { useState } from 'react';
import { X, FileText, CheckCircle, AlertTriangle, Clock, Leaf, Shield, Network, Lock, Star, Globe, Zap } from 'lucide-react';
import { ActiveContract, AvailableContract, LocationData, ClientType, Server } from '../types';
import { CONTRACT_TEMPLATES, SERVER_TYPES } from '../constants';

interface ContractsModalProps {
  onClose: () => void;
  availableContracts: AvailableContract[];
  activeContracts: ActiveContract[];
  locations: Record<string, LocationData>;
  greenCertifiedLocations: string[];
  gameDay: number;
  reputation: number;
  onAccept: (offer: AvailableContract) => void;
  onTerminate: (contractId: string) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const TIER_COLORS: Record<string, string> = {
  bronze: 'text-amber-500 bg-amber-900/20 border-amber-700/60',
  silver: 'text-slate-300 bg-slate-800/60 border-slate-600',
  gold: 'text-yellow-400 bg-yellow-900/20 border-yellow-700/60',
  platinum: 'text-violet-300 bg-violet-900/20 border-violet-600/60',
  hyperscale: 'text-cyan-300 bg-cyan-900/20 border-cyan-500/60',
};

const TIER_BADGE: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  hyperscale: 'Hyperscale',
};

const CLIENT_TYPE_CONFIG: Record<ClientType, { label: string; color: string }> = {
  streaming:    { label: 'Streaming',    color: 'text-purple-400' },
  finance:      { label: 'Finance',      color: 'text-blue-400' },
  government:   { label: 'Government',   color: 'text-indigo-400' },
  saas:         { label: 'SaaS',         color: 'text-emerald-400' },
  research:     { label: 'Research',     color: 'text-orange-400' },
  ecommerce:    { label: 'E-Commerce',   color: 'text-pink-400' },
  gaming:       { label: 'Gaming',       color: 'text-red-400' },
  vpn:          { label: 'VPN',          color: 'text-cyan-400' },
  healthcare:   { label: 'Healthcare',   color: 'text-teal-400' },
  education:    { label: 'Education',    color: 'text-yellow-400' },
  telecom:      { label: 'Telecom',      color: 'text-sky-400' },
  cybersecurity:{ label: 'Cyber',        color: 'text-rose-400' },
};

const SERVER_TIER_NAMES: Record<number, string> = {
  0: 'Entry (R250+)',
  1: 'Mid-range (R450+)',
  2: 'Performance (R650+)',
  3: 'High-end (R750+)',
  4: 'GPU (R750xa+)',
  5: 'GPU Pro (R760xa+)',
  6: 'AI (XE9680)',
};

// Find a server by ID within a location
function findServer(location: LocationData, serverId: string): Server | undefined {
  for (const floor of Object.values(location.floors ?? {})) {
    for (const rack of floor.racks ?? []) {
      const s = rack.servers?.find(sv => sv.id === serverId);
      if (s) return s;
    }
  }
  return undefined;
}

const ContractsModal: React.FC<ContractsModalProps> = ({
  onClose,
  availableContracts,
  activeContracts,
  locations,
  greenCertifiedLocations,
  gameDay,
  reputation,
  onAccept,
  onTerminate,
  formatCurrency,
}) => {
  const [tab, setTab] = useState<'marketplace' | 'active'>('marketplace');

  const locationName = (id: string) => locations[id]?.name ?? id;

  // Count idle servers at a location matching a minimum tier
  const countIdleServers = (locationId: string, minTier: number): number => {
    const loc = locations[locationId];
    if (!loc) return 0;
    let count = 0;
    Object.values(loc.floors ?? {}).forEach(floor => {
      floor.racks.forEach(rack => {
        rack.servers.forEach(server => {
          if (server.contractId) return;
          if (server.failureStatus === 'failed') return;
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (st && st.unlockLevel >= minTier) count++;
        });
      });
    });
    return count;
  };

  // Count total servers at a location matching a minimum tier (including contracted)
  const countTotalServers = (locationId: string, minTier: number): number => {
    const loc = locations[locationId];
    if (!loc) return 0;
    let count = 0;
    Object.values(loc.floors ?? {}).forEach(floor => {
      floor.racks.forEach(rack => {
        rack.servers.forEach(server => {
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (st && st.unlockLevel >= minTier) count++;
        });
      });
    });
    return count;
  };

  const hasNawas = (locationId: string) => !!locations[locationId]?.nawasSubscription;
  const hasRedundantTor = (locationId: string) =>
    Object.values(locations[locationId]?.floors ?? {}).some(f => (f.racks ?? []).some(r => !!r.torSwitchRedundant));

  const canAccept = (offer: AvailableContract): { ok: boolean; reasons: string[] } => {
    const t = offer.template;
    const loc = locations[offer.locationId];
    const reasons: string[] = [];
    if (!loc?.unlocked) { reasons.push('Location not unlocked'); return { ok: false, reasons }; }

    if (t.multiLocation) {
      const numLocs = t.requiredLocations ?? 2;
      const qualifyingLocs = Object.keys(locations)
        .filter(locId => locations[locId]?.unlocked && countIdleServers(locId, t.minServerTier) > 0)
        .length;
      if (qualifyingLocs < numLocs) {
        reasons.push(`Requires ${numLocs} locations with idle qualifying servers (you have ${qualifyingLocs})`);
      }
    } else {
      const idle = countIdleServers(offer.locationId, t.minServerTier);
      if (idle === 0) reasons.push(`No idle ${SERVER_TIER_NAMES[t.minServerTier] ?? 'matching'} servers available`);
    }
    if (t.requiresGreenCert && !greenCertifiedLocations.includes(offer.locationId))
      reasons.push('Green energy certification required');
    if (t.requiresNawas && !hasNawas(offer.locationId))
      reasons.push('NaWas DDoS protection required');
    if (t.requiresRedundantTor && !hasRedundantTor(offer.locationId))
      reasons.push('MLAG redundant TOR switch required');
    if (t.minReputation && reputation < t.minReputation)
      reasons.push(`Requires ${t.minReputation} reputation (you have ${Math.floor(reputation)})`);
    const duplicate = activeContracts.find(c => c.templateId === t.id && c.locationId === offer.locationId && c.active);
    if (duplicate) reasons.push('Already have this contract at this location');
    return { ok: reasons.length === 0, reasons };
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-3xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-white">Contracts</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 shrink-0">
          {(['marketplace', 'active'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t ? 'border-emerald-400 text-emerald-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {t === 'marketplace' ? `Marketplace (${availableContracts.length})` : `Active (${activeContracts.filter(c => c.active).length})`}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === 'marketplace' && (
            <>
              <p className="text-xs text-gray-500">
                Servers earn <strong className="text-white">$0</strong> without a contract. All hosting revenue comes from contracted server slots.
                Marketplace refreshes every 7 in-game days.
              </p>
              {availableContracts.length === 0 && (
                <div className="text-center text-gray-500 py-10">No contracts available right now. Check back in a few days.</div>
              )}
              {availableContracts.map((offer, i) => {
                const t = offer.template;
                const { ok, reasons } = canAccept(offer);
                const daysLeft = offer.expiresOnDay - gameDay;
                const totalValue = t.requiredServerCount * t.revenuePerServerPerDay * t.durationDays;
                const idle = countIdleServers(offer.locationId, t.minServerTier);
                const total = countTotalServers(offer.locationId, t.minServerTier);
                const clientCfg = CLIENT_TYPE_CONFIG[t.clientType] ?? { label: t.clientType, color: 'text-gray-400' };
                return (
                  <div key={i} className={`border rounded-lg p-4 ${TIER_COLORS[t.tier] ?? 'border-gray-600 bg-gray-800'}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Header row */}
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-bold px-2 py-0.5 rounded bg-black/30 text-current">{TIER_BADGE[t.tier]}</span>
                          <span className={`text-xs font-medium ${clientCfg.color}`}>{clientCfg.label}</span>
                          {t.requiresGreenCert && (
                            <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-900/40 px-1.5 py-0.5 rounded">
                              <Leaf size={9} /> Green cert
                            </span>
                          )}
                          {t.requiresNawas && (
                            <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-900/40 px-1.5 py-0.5 rounded">
                              <Shield size={9} /> NaWas
                            </span>
                          )}
                          {t.requiresRedundantTor && (
                            <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-900/40 px-1.5 py-0.5 rounded">
                              <Network size={9} /> MLAG
                            </span>
                          )}
                          {t.minReputation && (
                            <span className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${reputation >= t.minReputation ? 'text-yellow-400 bg-yellow-900/40' : 'text-red-400 bg-red-900/40'}`}>
                              <Star size={9} /> Rep {t.minReputation}+
                            </span>
                          )}
                          {t.multiLocation && (
                            <span className="flex items-center gap-1 text-xs text-cyan-400 bg-cyan-900/40 px-1.5 py-0.5 rounded font-semibold">
                              <Globe size={9} /> Multi-DC ×{t.requiredLocations}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
                            <Clock size={10} /> {daysLeft}d left
                          </span>
                        </div>

                        <div className="font-semibold text-white">{t.clientName}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{t.description}</div>
                        <div className="text-xs text-gray-500 mt-1">Location: <span className="text-gray-300">{locationName(offer.locationId)}</span></div>

                        {/* Economics */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                          <div>
                            <span className="text-gray-500">Revenue</span>
                            <span className="ml-2 font-semibold text-green-400">{formatCurrency(t.revenuePerServerPerDay)}/server/day</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Duration</span>
                            <span className="ml-2 font-semibold">{t.durationDays} days</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Total value</span>
                            <span className="ml-2 font-semibold text-emerald-400">{formatCurrency(totalValue)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Breach penalty</span>
                            <span className="ml-2 font-semibold text-red-400">{formatCurrency(t.penaltyPerBreach)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Traffic/server</span>
                            <span className="ml-2 font-semibold text-blue-400">
                              {t.trafficGbpsPerServer >= 1 ? `${t.trafficGbpsPerServer} Gbps` : `${(t.trafficGbpsPerServer * 1000).toFixed(0)} Mbps`}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Total traffic</span>
                            <span className="ml-2 font-semibold text-blue-300">
                              {(() => { const g = t.trafficGbpsPerServer * t.requiredServerCount; return g >= 1 ? `${g.toFixed(1)} Gbps` : `${(g * 1000).toFixed(0)} Mbps`; })()}
                            </span>
                          </div>
                        </div>

                        {/* Requirements */}
                        <div className="mt-3 space-y-1 text-xs">
                          {t.multiLocation ? (
                            <div className="flex items-center gap-1.5 text-cyan-400">
                              <Globe size={11} />
                              {t.requiredServerCount}× {SERVER_TIER_NAMES[t.minServerTier] ?? 'servers'} per location × {t.requiredLocations} DCs — auto-assigned across your best locations
                            </div>
                          ) : (
                          <div className={`flex items-center gap-1.5 ${idle >= t.requiredServerCount ? 'text-emerald-400' : idle > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                            {idle >= t.requiredServerCount ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                            {t.requiredServerCount}× {SERVER_TIER_NAMES[t.minServerTier] ?? 'servers'} required
                            <span className="text-gray-500 ml-1">({idle} idle / {total} total at location)</span>
                          </div>
                          )}
                          {t.requiresGreenCert && (
                            <div className={`flex items-center gap-1.5 ${greenCertifiedLocations.includes(offer.locationId) ? 'text-emerald-400' : 'text-red-400'}`}>
                              {greenCertifiedLocations.includes(offer.locationId) ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                              Green energy certification
                            </div>
                          )}
                          {t.requiresNawas && (
                            <div className={`flex items-center gap-1.5 ${hasNawas(offer.locationId) ? 'text-emerald-400' : 'text-red-400'}`}>
                              {hasNawas(offer.locationId) ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                              NaWas DDoS protection
                            </div>
                          )}
                          {t.requiresRedundantTor && (
                            <div className={`flex items-center gap-1.5 ${hasRedundantTor(offer.locationId) ? 'text-emerald-400' : 'text-red-400'}`}>
                              {hasRedundantTor(offer.locationId) ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                              MLAG redundant TOR switch
                            </div>
                          )}
                          <div className="text-gray-500">SLA: {(t.uptimeRequired * 100).toFixed(2)}% uptime</div>
                        </div>

                        {!ok && reasons.length > 0 && (
                          <div className="mt-2 text-xs text-amber-400">{reasons.join(' · ')}</div>
                        )}
                        {ok && idle < t.requiredServerCount && (
                          <div className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                            <AlertTriangle size={11} /> Only {idle}/{t.requiredServerCount} servers available — partial revenue until more are installed
                          </div>
                        )}
                      </div>

                      <button
                        disabled={!ok}
                        onClick={() => onAccept(offer)}
                        className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          ok ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Accept
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {tab === 'active' && (
            <>
              {activeContracts.filter(c => c.active).length === 0 && (
                <div className="text-center text-gray-500 py-10">No active contracts. Accept offers from the Marketplace tab.</div>
              )}
              {activeContracts.filter(c => c.active).map((contract, i) => {
                const daysRemaining = contract.endDay - gameDay;
                const progress = Math.max(0, Math.min(1, 1 - daysRemaining / (contract.endDay - contract.startDay)));
                const loc = locations[contract.locationId];
                const clientCfg = CLIENT_TYPE_CONFIG[contract.clientType] ?? { label: contract.clientType, color: 'text-gray-400' };

                // Count operational vs failed among assigned servers (multi-location aware)
                let operational = 0, failed = 0;
                if (contract.locationAssignments) {
                  Object.entries(contract.locationAssignments).forEach(([locId, sids]) => {
                    const l = locations[locId];
                    sids.forEach(sid => {
                      const server = l ? findServer(l, sid) : undefined;
                      if (!server) return;
                      server.failureStatus === 'failed' ? failed++ : operational++;
                    });
                  });
                } else {
                  contract.assignedServerIds.forEach(sid => {
                    const server = loc ? findServer(loc, sid) : undefined;
                    if (!server) return;
                    server.failureStatus === 'failed' ? failed++ : operational++;
                  });
                }
                const currentDailyRevenue = operational * contract.revenuePerServerPerDay;
                const provisioned = contract.assignedServerIds.length;
                const needed = contract.requiredServerCount;

                return (
                  <div key={i} className={`border rounded-lg p-4 ${TIER_COLORS[contract.tier] ?? 'border-gray-600 bg-gray-800'}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-bold px-2 py-0.5 rounded bg-black/30">{TIER_BADGE[contract.tier]}</span>
                          <span className={`text-xs font-medium ${clientCfg.color}`}>{clientCfg.label}</span>
                          <CheckCircle size={12} className="text-emerald-400" />
                          <span className="text-xs text-emerald-400">Active</span>
                          {contract.breachCount > 0 && (
                            <span className="text-xs text-red-400 flex items-center gap-1 ml-1">
                              <AlertTriangle size={10} /> {contract.breachCount} breach{contract.breachCount > 1 ? 'es' : ''}
                            </span>
                          )}
                        </div>

                        <div className="font-semibold text-white">{contract.clientName}</div>
                        {contract.locationAssignments ? (
                          <div className="text-xs text-cyan-400 mt-0.5 flex items-center gap-1">
                            <Globe size={10} />
                            Multi-DC: {Object.entries(contract.locationAssignments).map(([locId, sids]) =>
                              `${locationName(locId)} (${sids.length})`
                            ).join(' · ')}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 mt-0.5">Location: {locationName(contract.locationId)}</div>
                        )}

                        {/* Server provisioning status */}
                        <div className="mt-2 text-xs">
                          <div className={`flex items-center gap-1.5 ${provisioned >= needed ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {provisioned >= needed ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                            <span className="font-medium">{provisioned}/{needed} servers provisioned</span>
                            {failed > 0 && <span className="text-red-400 ml-2">· {failed} failed</span>}
                          </div>
                        </div>

                        {/* Economics grid */}
                        {(() => {
                          const tmpl = CONTRACT_TEMPLATES.find(t => t.id === contract.templateId);
                          const trafficPerServer = tmpl?.trafficGbpsPerServer ?? 0;
                          const totalTrafficGbps = trafficPerServer * provisioned;
                          return (
                            <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                              <div>
                                <div className="text-gray-500">Rate</div>
                                <div className="font-semibold text-white">{formatCurrency(contract.revenuePerServerPerDay)}/server</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Current daily</div>
                                <div className={`font-semibold ${currentDailyRevenue > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                                  {formatCurrency(currentDailyRevenue)}/day
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500">Days left</div>
                                <div className="font-semibold">{Math.max(0, daysRemaining)}d</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Total earned</div>
                                <div className="font-semibold text-emerald-400">{formatCurrency(contract.totalEarned)}</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Contract value</div>
                                <div className="font-semibold text-gray-300">{formatCurrency(needed * contract.revenuePerServerPerDay * (contract.endDay - contract.startDay))}</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Breach penalty</div>
                                <div className="font-semibold text-red-400">{formatCurrency(contract.penaltyPerBreach)}</div>
                              </div>
                              {trafficPerServer > 0 && (
                                <div className="col-span-3">
                                  <div className="text-gray-500">Traffic demand</div>
                                  <div className="font-semibold text-blue-400">
                                    {trafficPerServer >= 1 ? `${trafficPerServer} Gbps` : `${(trafficPerServer * 1000).toFixed(0)} Mbps`}/server
                                    <span className="text-blue-300 ml-2">= {totalTrafficGbps >= 1 ? `${totalTrafficGbps.toFixed(1)} Gbps` : `${(totalTrafficGbps * 1000).toFixed(0)} Mbps`} total</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Progress bar */}
                        <div className="mt-3 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress * 100}%` }} />
                        </div>

                        {/* Assigned servers list (compact) */}
                        {contract.assignedServerIds.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {contract.assignedServerIds.map(sid => {
                              const server = loc ? findServer(loc, sid) : undefined;
                              const st = server ? SERVER_TYPES.find(s => s.id === server.type) : undefined;
                              const isFailed = server?.failureStatus === 'failed';
                              return (
                                <span
                                  key={sid}
                                  className={`text-xs px-1.5 py-0.5 rounded ${isFailed ? 'bg-red-900/40 text-red-400' : 'bg-gray-700 text-gray-300'}`}
                                  title={server ? `${st?.name ?? server.type} — ${isFailed ? 'FAILED' : 'operational'}` : 'Server not found'}
                                >
                                  {st?.name ?? '?'}{isFailed ? ' ✗' : ''}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => onTerminate(contract.templateId + '|' + contract.locationId)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-xs bg-gray-700 hover:bg-red-900 text-gray-300 hover:text-red-300 transition-colors"
                      >
                        Terminate
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Completed / Terminated */}
              {activeContracts.filter(c => !c.active).length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-2">Completed / Terminated</div>
                  {activeContracts.filter(c => !c.active).slice(0, 5).map((contract, i) => (
                    <div key={i} className="border border-gray-700 rounded-lg p-3 mb-2 opacity-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-gray-300">{contract.clientName}</span>
                          <span className="text-xs text-gray-500 ml-2">— {locationName(contract.locationId)}</span>
                        </div>
                        <span className="text-xs text-gray-500">Earned: {formatCurrency(contract.totalEarned)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer info */}
        <div className="px-5 py-3 border-t border-gray-700 shrink-0">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1"><Zap size={10} className="text-amber-400" /> Servers without a contract earn $0/day</div>
            <div className="flex items-center gap-1"><CheckCircle size={10} className="text-emerald-400" /> Contracted servers earn full rate while operational</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractsModal;
